// Classificazione di una partita EA Pro Clubs: distingue i risultati canonici
// (win/loss/draw + win/loss per DNF già registrato da EA) dalle partite
// "non terminate" — quelle in cui il `result` di entrambi i club è 0 perché
// EA non ha consolidato l'esito (tipicamente: uno dei due ha quittato e la
// API non ha codificato il DNF). Il punteggio mostrato in quei casi non è
// confermabile.

export type EaMatchOutcome =
  | "win"
  | "loss"
  | "draw"
  | "win_dnf"        // avversario quittato, EA ha codificato il forfait
  | "loss_dnf"       // noi quittati, EA ha codificato il forfait
  | "unterminated";  // result=0/0 — esito non consolidato da EA

// Bit "winner by DNF" nel result EA: presente quando una squadra vince per
// abbandono dell'avversaria. Valori tipici: 16385 (= 1 + 16384, win + dnf).
const DNF_WIN_BIT = 16384;

export function classifyEaMatch(
  matchData: any,
  ourClubId: string,
): EaMatchOutcome {
  const clubs = matchData?.clubs || {};
  const ours: any = clubs[ourClubId];
  if (!ours) return "unterminated";
  const oppId = Object.keys(clubs).find((id) => id !== ourClubId);
  const opp: any = oppId ? clubs[oppId] : null;

  const myResult = Number(ours.result ?? -1);
  const oppResult = opp ? Number(opp.result ?? -1) : -1;

  // EA ha registrato un forfait esplicito (codice 10 = team che ha quittato)
  if (myResult === 10) return "loss_dnf";
  if (oppResult === 10) return "win_dnf";
  // Oppure il bit "winner by DNF" sul risultato nostro
  if ((myResult & DNF_WIN_BIT) !== 0) return "win_dnf";

  // Pareggio canonico (giocato 90 minuti, EA lo codifica come 4 da entrambe)
  if (myResult === 4 && oppResult === 4) return "draw";

  // Win/loss canonici
  if (myResult === 1) return "win";
  if (myResult === 2) return "loss";

  // result=0 di entrambi = partita non terminata. Anche il caso "uno solo
  // a 0" è sospetto e lo trattiamo come non terminata per prudenza.
  if (myResult === 0 || oppResult === 0) return "unterminated";

  // Codice sconosciuto: la trattiamo come non terminata per non far passare
  // numeri non confermati come fossero risultati ufficiali.
  return "unterminated";
}

// Soglia: una partita giocata ha distribuzione naturale dei voti (chi gioca
// bene 8+, chi male 5-). Da dati Ca De Rissi la "moda" dei voti per squadra
// in una partita giocata ha al massimo 3 player con voto identico; nelle
// partite non giocate la moda esplode a 11 (tutti default) o quasi.
// Soglia ">3" (cioe' ≥4) è il primo valore che separa pulito i due mondi
// senza falsi positivi sui dati reali.
const UNPLAYED_MODE_THRESHOLD = 3;

function maxIdenticalRatings(players: any): number {
  if (!players || typeof players !== "object") return 0;
  const counts = new Map<number, number>();
  for (const p of Object.values(players) as any[]) {
    const r = Number(p?.rating);
    if (!Number.isFinite(r) || r <= 0) continue;
    counts.set(r, (counts.get(r) || 0) + 1);
  }
  let max = 0;
  for (const c of counts.values()) if (c > max) max = c;
  return max;
}

/**
 * Una partita risulta "non disputata" quando in ENTRAMBE le squadre più di
 * 3 giocatori hanno voto EA identico. EA assegna voti realistici e variabili
 * a chi gioca davvero: avere 4+ giocatori con lo stesso voto preciso in
 * entrambi i club è statisticamente anomalo e indica:
 *  - forfait totale (tutti a 6.0 default)
 *  - blocco/penalizzazione mid-match (es. gruppi a 3.0 quando uno scollega)
 *  - no-contest
 *
 * La simmetria (entrambe le squadre, non una sola) elimina i falsi positivi
 * tipo "solo noi siamo andati in lag": se solo una squadra è impattata
 * spesso è un problema locale, non un dato non disputato a livello partita.
 *
 * Vale sia per amichevoli che per league/playoff: regola unica.
 */
export function isMatchUnplayed(matchData: any, ourClubId: string): boolean {
  const clubs = matchData?.clubs || {};
  const oppId = Object.keys(clubs).find((id) => id !== ourClubId);
  if (!oppId) return false;
  const players = matchData?.players || {};
  const ourMax = maxIdenticalRatings(players[ourClubId]);
  const oppMax = maxIdenticalRatings(players[oppId]);
  return ourMax > UNPLAYED_MODE_THRESHOLD && oppMax > UNPLAYED_MODE_THRESHOLD;
}
